import { Checkmark } from '@carbon/icons-react';
import type { UserProfile } from '@repo/shared/orpc/types';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox, HoverView, Pad, PadBox } from 'component/basics';
import { FacePile } from 'component/people';
import { UtilityText } from 'component/text';
import { View } from 'react-native';

import { PercentageBar, PercentageBarContainer } from './videovotingPercentageBars';

/** @public */
export type OptionRadioButtonProps = {
    selected?: boolean;
    onPress?: () => void;
    testID?: string;
};

// The radio button needs to be an own component for everyone

// TODO This is copied from the poll picker component
function OptionRadioButton({ selected, onPress, testID }: OptionRadioButtonProps) {
    const s = useOptionRadioButtonStyles();
    return (
        <HoverView onPress={onPress} testID={testID}>
            <View style={[s.rightIcon, selected ? s.iconSelected : '']}>
                <Checkmark />
            </View>
        </HoverView>
    );
}

const useOptionRadioButtonStyles = createThemedStyles((tokens) => ({
    body: {
        flex: 1,
    },
    rightIcon: {
        borderColor: tokens.colors.colorBorderPrimary,
        borderRadius: 100,
        borderWidth: 1,
        padding: 4,
        color: tokens.colors.colorSurfacePrimary,
    },
    iconSelected: {
        backgroundColor: tokens.colors.colorTextPrimary,
        borderColor: tokens.colors.colorTextPrimary,
        color: tokens.colors.colorTextOnPrimaryButton,
    },
}));

export type VideoVotingOptionProps = {
    optionText: string;
    selected?: boolean;
    voteCount?: number;
    allVoteCount?: number;
    voters?: UserProfile[];
    onSelect?: () => void;
    votingEnabled?: boolean;
    showResults?: boolean;
};

export function VideoVotingOption({
    optionText,
    selected,
    voteCount = 0,
    allVoteCount = 0,
    voters = [],
    onSelect,
    votingEnabled,
    showResults,
}: VideoVotingOptionProps) {
    const { colors } = useTheme();
    const s = useVideoVotingOptionStyles();
    const percentage = allVoteCount > 0 ? (voteCount * 100) / allVoteCount : 0;

    return (
        <PadBox bottom={20}>
            <HorizBox center>
                <View style={{ flex: 1 }}>
                    <HorizBox center spread>
                        <View style={{ flex: 1 }}>
                            <UtilityText text={optionText} />
                        </View>
                        <View style={{ opacity: showResults ? 1 : 0 }}>
                            <HorizBox center>
                                <View style={{ position: 'relative', left: 10 }} testID={`voters-${optionText}`}>
                                    <FacePile profiles={voters} type='tiny' />
                                </View>
                                <View style={s.tag} testID={`vote-count-${optionText}`}>
                                    <UtilityText color={colors.colorTextOnTagLight} text={voteCount.toString()} type='small' />
                                </View>
                            </HorizBox>
                        </View>
                    </HorizBox>
                    <Pad size={4} />
                    <View style={[{ flexGrow: 1, position: 'relative', opacity: showResults ? 1 : 0 }, s.percentageBarBase]}>
                        <PercentageBarContainer backgroundColor='#d6d6d6'>
                            <PercentageBar colors={[selected ? colors.colorDataVizScale2 : colors.colorGrey5]} percentages={[percentage]} />
                        </PercentageBarContainer>
                    </View>
                </View>
                {votingEnabled ? (
                    <View style={{ marginLeft: 16 }}>
                        <OptionRadioButton onPress={onSelect} selected={selected} testID={optionText} />
                    </View>
                ) : null}
            </HorizBox>
        </PadBox>
    );
}

const useVideoVotingOptionStyles = createThemedStyles((tokens) => ({
    percentageBarBase: {
        height: 7,
        borderRadius: 5.5,
        overflow: 'hidden',
    },
    tag: {
        display: 'flex',
        borderRadius: 100,
        borderWidth: 2,
        borderColor: tokens.colors.colorSurfaceSecondary,
        backgroundColor: '#C3C3C3',
        paddingHorizontal: 6,
        height: 24,
        justifyContent: 'center',
        alignItems: 'center',
    },
}));
